import { c as _c } from "react/compiler-runtime";
import type { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ItemCard, ItemCardBody, ItemCardMeta, ItemCardSection, ItemCardSummary } from '@sola/ark-core/molecules/item-card/item-card.tsx';
import '@sola/locales/formatters.ts';
import { CommitmentAvatar } from '../../atoms/commitment-avatar/commitment-avatar.tsx';
import { PledgeIcon } from '../../atoms/pledge-icon/pledge-icon.tsx';
import { mapFundCodeToCommitmentType } from '../../organisms/giving-history/giving-history.logic.ts';
import type { GivingItemWithCommitment } from '../../utils/giving-history.ts';
import { getHistoryItemData } from './giving-history-card.logic.ts';
export type GivingHistoryCardProps = {
  givingItem: GivingItemWithCommitment;
};
const getIcon = (givingItem: GivingItemWithCommitment) => {
  if (givingItem.commitment) {
    return <CommitmentAvatar responsive commitment={givingItem.commitment} />;
  }
  if (givingItem.pledge) {
    return <PledgeIcon {...givingItem.pledge} />;
  }

  // Some giving items have enought data to craft an icon even without a commitment
  return <CommitmentAvatar responsive commitment={{
    commitmentType: mapFundCodeToCommitmentType(givingItem.fundCode),
    fund: {
      code: givingItem.fundCode,
      icon: givingItem.fundIcon
    }
  }} />;
};
const useComponentData = (args: GivingHistoryCardProps & {
  t: TFunction;
}) => {
  const {
    givingItem,
    t
  } = args;
  const {
    title,
    label,
    rate
  } = getHistoryItemData(givingItem, t);
  const icon = getIcon(givingItem);
  return {
    icon,
    title,
    label,
    rate
  };
};

/**
 * Commitment card, shown for both commitments and giving history items.
 */
export const GivingHistoryCard: React.FC<GivingHistoryCardProps> = props => {
  const $ = _c(16);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["my", "core"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  let t1;
  if ($[1] !== props || $[2] !== t) {
    t1 = {
      t,
      ...props
    };
    $[1] = props;
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const cd = useComponentData(t1);
  let t2;
  if ($[4] !== cd.rate || $[5] !== t) {
    t2 = typeof cd.rate === "string" ? cd.rate : t("core:numbers.currencyCustom", {
      amount: cd.rate,
      useNegativeParens: true
    });
    $[4] = cd.rate;
    $[5] = t;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const rate = t2;
  let t3;
  if ($[7] !== cd.icon || $[8] !== cd.label || $[9] !== cd.title) {
    t3 = <ItemCardSummary icon={cd.icon} title={cd.title} subTitle={cd.label} />;
    $[7] = cd.icon;
    $[8] = cd.label;
    $[9] = cd.title;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== rate) {
    t4 = <ItemCardMeta text={rate} />;
    $[11] = rate;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t3 || $[14] !== t4) {
    t5 = <ItemCard><ItemCardSection><ItemCardBody>{t3}{t4}</ItemCardBody></ItemCardSection></ItemCard>;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
};